import {
  genericGetSingle,
  genericGetAll,
  genericUpdateSingle,
  genericDeleteSingle,
  genericCreateSingle,
  makeAPIRequest,
} from "../helpers/apiCall";
import { Error } from "../types";
import { iOrder } from "../models";
import { parseJSONData } from "../helpers/json";

/**
 * Get all iOrders
 * @param params URLSearchParams
 * @returns Promise<{ orders: iOrder[] }>
 */
function getAll(params: URLSearchParams = null): Promise<{ orders: iOrder[] }> {
  return genericGetAll("i_orders", params);
}

/**
 * Get a single iOrder
 * @param id number | string
 * @returns Promise<{ order: iOrder }>
 */
function getSingle(id: number | string): Promise<{ order: iOrder }> {
  return genericGetSingle("i_orders", id).then((response) => {
    if (response?.order?.data) {
      response.order.loadedData = parseJSONData(response.order.data);
    }
    return response;
  });
}

/**
 * Update a single iOrder
 * @param id number | string
 * @param order { [key: string]: string | number | boolean | Date | undefined}
 * @returns Promise<{ order: iOrder } | Error>
 */
function updateSingle(
  id: number | string,
  order: { [key: string]: string | number | boolean | Date | undefined },
): Promise<{ order: iOrder } | Error> {
  return genericUpdateSingle("i_orders", id, order).then((response) => {
    if (response?.order?.data) {
      response.order.loadedData = parseJSONData(response.order.data);
    }
    return response;
  });
}

/**
 * Delete a single iOrder
 * @param id number | string
 * @returns Promise<{ deleted: boolean } | { error: string }>
 */
function deleteSingle(
  id: number | string,
): Promise<{ deleted: boolean } | { error: string }> {
  return genericDeleteSingle("i_orders", id);
}

/**
 * Gets the count of iOrders
 * @param params URLSearchParams
 * @returns Promise<{ count: number }>
 */
function getCount(params: URLSearchParams = null): Promise<{ count: number }> {
  params.append("countOnly", "true");
  return genericGetAll("i_orders", params);
}

/**
 * Create a new iOrder
 * @param iOrder { [key: string]: string | number | boolean | Date | undefined}
 * @returns Promise<{ order: iOrder } | Error>
 */
function createSingle(iOrder: {
  [key: string]: string | number | boolean | Date | undefined;
}): Promise<{ order: iOrder } | Error> {
  return genericCreateSingle("i_orders", iOrder);
}

/**
 * Refreshes the order lines for a given order by its ID. The API only accepts iOrders
 * with a type of "Replenishment" that have not been sent to power pick.
 *
 * Sends a PUT request to the `/i_orders/{id}/lines/refresh` endpoint to refresh the order lines.
 *
 * @param id - The unique identifier of the order to refresh. Can be a number or a string.
 * @returns A promise that resolves to an object containing the refreshed `iOrder`.
 * @throws Will throw an error if the API request fails.
 */
function refreshOrder(id: number | string): Promise<{ order: iOrder }> {
  return makeAPIRequest({ url: `/i_orders/${id}/lines/refresh`, method: "put" })
    .then((response) => {
      if (response?.data?.order?.data) {
        response.data.order.loadedData = parseJSONData(
          response.data.order.data,
        );
      }
      if (response?.data?.order?.lines) {
        response.data.order.lines.forEach((line) => {
          line.loadedData = parseJSONData(line?.data);
        });
      }
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
}

export const iOrderService = {
  getAll,
  getSingle,
  updateSingle,
  deleteSingle,
  getCount,
  createSingle,
  refreshOrder,
};
